import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft,
  Upload,
  ImageIcon,
  X,
  RefreshCw,
  ArrowRight,
  Loader2,
  CheckCircle2,
  AlertTriangle,
} from "lucide-react";
import { useSEO } from "@/lib/useSEO";
import { AnalyzerReview } from "./analyzer/AnalyzerReview";
import { AnalyzerDataCheck } from "./analyzer/AnalyzerDataCheck";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const EXTRACT_ENDPOINT = `${BACKEND_URL}/api/analyzer/extract-slip`;

const TITLE = "Free Bet Slip Analyzer | Parlay Assistant";
const DESCRIPTION =
  "Upload a screenshot of your bet slip and let Parlay Assistant analyze each supported leg using current market odds, historical player performance, PA Score, and plain-English explanations.";

const ACCEPT = "image/png,image/jpeg,image/jpg,image/webp";
const ACCEPT_LABEL = "PNG, JPG, JPEG or WEBP";
const MAX_MB = 10;
const MAX_BYTES = MAX_MB * 1024 * 1024;

const isAcceptedType = (file) =>
  /^image\/(png|jpe?g|webp)$/i.test(file?.type || "");

const formatBytes = (n) => {
  if (!Number.isFinite(n)) return "";
  if (n >= 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(2)} MB`;
  if (n >= 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${n} B`;
};

export const validateFile = (f) => {
  if (!f) return "Please select a file.";
  if (!isAcceptedType(f)) return "Please upload a PNG, JPG, JPEG, or WEBP image.";
  if (f.size > MAX_BYTES)
    return "Your image is too large. Please upload an image under 10 MB.";
  return "";
};

export default function AnalyzerPage() {
  useSEO({ title: TITLE, description: DESCRIPTION, path: "/analyzer" });

  const inputRef = useRef(null);
  // "upload" | "image_confirmed" | "analyzing" | "review" | "unreadable" | "confirmed_success"
  const [step, setStep] = useState("upload");
  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState("");
  const [isDragging, setIsDragging] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [extractedSlip, setExtractedSlip] = useState(null);
  const [extractionMessage, setExtractionMessage] = useState("");

  const hasFile = !!file && !!previewUrl;

  const openFileDialog = () => inputRef.current?.click();

  const acceptFile = (f) => {
    const err = validateFile(f);
    if (err) {
      setErrorMsg(err);
      return;
    }
    let url;
    try {
      url = URL.createObjectURL(f);
    } catch {
      setErrorMsg("PA could not preview this image. Please try another file.");
      return;
    }
    if (!url) {
      setErrorMsg("PA could not preview this image. Please try another file.");
      return;
    }
    setErrorMsg("");
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setFile(f);
    setPreviewUrl(url);
  };

  const handleInputChange = (e) => {
    const f = e.target.files?.[0];
    acceptFile(f);
    if (inputRef.current) inputRef.current.value = "";
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    const f = e.dataTransfer?.files?.[0];
    acceptFile(f);
  };

  const clearFile = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setFile(null);
    setPreviewUrl("");
    setErrorMsg("");
    setExtractedSlip(null);
    setExtractionMessage("");
    setStep("upload");
  };

  const goContinue = () => {
    if (!hasFile) return;
    setStep("image_confirmed");
  };

  const goBackToPreview = () => setStep("upload");

  const goBackToImageFromReview = () => {
    setStep("image_confirmed");
  };

  const readSlip = async () => {
    if (!hasFile || step === "analyzing") return;
    setStep("analyzing");
    setExtractionMessage("");
    try {
      const fd = new FormData();
      fd.append("image", file, file.name);
      const res = await fetch(EXTRACT_ENDPOINT, { method: "POST", body: fd });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const body = await res.json();
      if (body.ok && body.slip) {
        setExtractedSlip(body.slip);
        setStep("review");
      } else {
        setExtractionMessage(
          body?.message || "PA couldn't clearly read this slip.",
        );
        setStep("unreadable");
      }
    } catch (err) {
      setExtractionMessage("PA couldn't clearly read this slip.");
      setStep("unreadable");
    }
  };

  const confirmSlip = () => {
    setStep("confirmed_success");
  };

  const restartFromUnreadable = () => {
    // Keep the file but return to preview so the user can Replace.
    setExtractionMessage("");
    setStep("upload");
  };

  // Revoke any live blob URL when the component unmounts to avoid leaks.
  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  // ---- Sub-views -----------------------------------------------------------

  const renderEmptyUpload = () => (
    <>
      <div
        className="mx-auto inline-flex items-center justify-center w-14 h-14 rounded-2xl"
        style={{
          background: "linear-gradient(135deg,#065f46,#10b981)",
          boxShadow: "0 10px 26px -12px rgba(16,185,129,0.6)",
        }}
      >
        <ImageIcon size={22} color="#ecfdf5" />
      </div>
      <p className="mt-4 font-display text-lg md:text-xl uppercase tracking-wide text-slate-900">
        Drop your bet slip here
      </p>
      <p className="mt-1 text-xs md:text-sm text-slate-500">
        {ACCEPT_LABEL} · up to {MAX_MB} MB
      </p>
      <div className="mt-5 flex flex-col sm:flex-row items-center justify-center gap-3">
        <button
          type="button"
          data-testid="analyzer-upload-btn"
          onClick={openFileDialog}
          className="pa-btn pa-btn-primary"
        >
          <Upload size={16} /> Upload Bet Slip
        </button>
      </div>
      <p className="mt-4 text-[11px] text-slate-500">
        Or drag and drop a file into this area.
      </p>
    </>
  );

  const renderPreviewState = () => (
    <div className="text-left" data-testid="analyzer-preview-state">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="min-w-0">
          <p
            data-testid="analyzer-file-name"
            className="font-display text-sm text-slate-900 truncate"
          >
            {file?.name || "Uploaded bet slip"}
          </p>
          <p
            data-testid="analyzer-file-size"
            className="text-xs text-slate-500 font-mono-num mt-0.5"
          >
            {formatBytes(file?.size)}
          </p>
        </div>
      </div>
      <img
        data-testid="analyzer-preview"
        src={previewUrl}
        alt="Bet slip preview"
        className="mt-3 max-h-[520px] w-auto mx-auto rounded-xl border border-slate-200 shadow-sm"
      />
      <div className="mt-4 flex flex-col sm:flex-row items-center justify-center gap-2">
        <button
          type="button"
          data-testid="analyzer-replace-btn"
          onClick={openFileDialog}
          className="pa-btn pa-btn-ghost"
        >
          <RefreshCw size={16} /> Replace Image
        </button>
        <button
          type="button"
          data-testid="analyzer-remove-btn"
          onClick={clearFile}
          className="pa-btn pa-btn-ghost"
        >
          <X size={16} /> Remove Image
        </button>
        <button
          type="button"
          data-testid="analyzer-continue-btn"
          onClick={goContinue}
          className="pa-btn pa-btn-primary"
        >
          Continue <ArrowRight size={16} />
        </button>
      </div>
    </div>
  );

  return (
    <main
      data-testid="analyzer-page"
      data-step={step}
      className="max-w-4xl mx-auto px-5 md:px-8 mt-10 md:mt-14"
    >
      <div className="mb-6">
        <Link
          to="/"
          className="pa-nav-link inline-flex items-center gap-2 text-sm text-slate-300 hover:text-white"
        >
          <ArrowLeft size={14} /> Back to Calculators
        </Link>
      </div>

      <section className="pa-card p-6 md:p-8">
        <p className="font-display text-emerald-700 text-xs tracking-[0.2em] uppercase">
          Bet Slip Tools · Beta
        </p>
        <h1 className="font-display text-3xl md:text-4xl uppercase tracking-tight text-slate-900 mt-1">
          PA Bet Slip Analyzer
        </h1>
        <p className="mt-3 text-slate-600 text-sm md:text-base">
          Upload your bet slip and let PA break down each leg.
        </p>

        {/* Uploader / preview / confirmed screenshot */}
        {step === "upload" && (
          <div
            data-testid="analyzer-drop-zone"
            onDragOver={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setIsDragging(true);
            }}
            onDragEnter={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setIsDragging(true);
            }}
            onDragLeave={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setIsDragging(false);
            }}
            onDrop={handleDrop}
            className="mt-6 rounded-2xl border-2 border-dashed p-6 md:p-10 text-center transition-colors"
            style={{
              borderColor: isDragging ? "#059669" : "#cbd5e1",
              background: isDragging ? "#ecfdf5" : "#f8fafc",
            }}
          >
            {hasFile ? renderPreviewState() : renderEmptyUpload()}
            <input
              ref={inputRef}
              type="file"
              data-testid="analyzer-file-input"
              accept={ACCEPT}
              onChange={handleInputChange}
              className="hidden"
            />
          </div>
        )}

        {step === "image_confirmed" && hasFile && (
          <div className="mt-6" data-testid="analyzer-confirmed-state">
            <img
              data-testid="analyzer-confirmed-image"
              src={previewUrl}
              alt="Confirmed bet slip"
              className="max-h-[420px] w-auto mx-auto rounded-xl border border-slate-200 shadow-sm"
            />
            <p className="mt-2 text-xs text-slate-500 text-center font-mono-num">
              {file.name} · {formatBytes(file.size)}
            </p>
          </div>
        )}

        {errorMsg && (
          <p
            data-testid="analyzer-error"
            className="mt-3 text-sm"
            style={{ color: "#b91c1c" }}
          >
            {errorMsg}
          </p>
        )}

        <p
          data-testid="analyzer-disclaimer"
          className="mt-6 text-xs text-slate-500 max-w-2xl"
        >
          PA provides research and informational analysis only. Results are not predictions or
          guarantees.
        </p>
      </section>

      {/* Step 2 confirmation card */}
      {step === "image_confirmed" && hasFile && (
        <section
          data-testid="analyzer-ready-card"
          className="pa-card p-6 md:p-8 mt-6 md:mt-8"
        >
          <h2 className="font-display text-xl md:text-2xl uppercase tracking-wide text-slate-900">
            Ready to Read Your Slip
          </h2>
          <p className="mt-2 text-slate-600 text-sm md:text-base">
            In the next step, PA will read the bets shown in this screenshot.
          </p>
          <div className="mt-5 flex flex-col sm:flex-row items-center gap-3">
            <button
              type="button"
              data-testid="analyzer-back-btn"
              onClick={goBackToPreview}
              className="pa-btn pa-btn-ghost"
            >
              <ArrowLeft size={16} /> Back
            </button>
            <button
              type="button"
              data-testid="analyzer-read-btn"
              onClick={readSlip}
              className="pa-btn pa-btn-primary"
            >
              Read My Slip
            </button>
          </div>
        </section>
      )}

      {/* Step 3a: Analyzing */}
      {step === "analyzing" && (
        <section
          data-testid="analyzer-analyzing"
          className="pa-card p-6 md:p-8 mt-6 md:mt-8 text-center"
          aria-busy="true"
          aria-live="polite"
        >
          <Loader2
            size={40}
            className="mx-auto animate-spin"
            style={{ color: "#059669" }}
          />
          <h2 className="mt-4 font-display text-xl md:text-2xl uppercase tracking-wide text-slate-900">
            PA is reading your slip...
          </h2>
          <p className="mt-2 text-slate-600 text-sm md:text-base">
            This may take a few seconds.
          </p>
        </section>
      )}

      {/* Step 3b: Review + editable legs */}
      {step === "review" && extractedSlip && (
        <AnalyzerReview
          slip={extractedSlip}
          onSlipChange={setExtractedSlip}
          onBack={goBackToImageFromReview}
          onConfirm={confirmSlip}
        />
      )}

      {/* Step 3c: Unreadable failure */}
      {step === "unreadable" && (
        <section
          data-testid="analyzer-unreadable"
          className="pa-card p-6 md:p-8 mt-6 md:mt-8"
        >
          <div className="flex items-start gap-3">
            <AlertTriangle size={24} style={{ color: "#b45309" }} />
            <div>
              <h2 className="font-display text-xl md:text-2xl uppercase tracking-wide text-slate-900">
                {extractionMessage || "PA couldn't clearly read this slip."}
              </h2>
              <p className="mt-2 text-slate-600 text-sm md:text-base">
                Try a clearer screenshot.
              </p>
            </div>
          </div>
          <div className="mt-5 flex flex-col sm:flex-row items-center gap-3">
            <button
              type="button"
              onClick={restartFromUnreadable}
              data-testid="analyzer-try-again-btn"
              className="pa-btn pa-btn-primary"
            >
              <RefreshCw size={16} /> Try Another Image
            </button>
          </div>
        </section>
      )}

      {/* Step 3d: Confirmed success */}
      {step === "confirmed_success" && (
        <section
          data-testid="analyzer-confirmed-success"
          className="pa-card p-6 md:p-8 mt-6 md:mt-8"
        >
          <div className="flex items-start gap-3">
            <CheckCircle2 size={28} style={{ color: "#059669" }} />
            <div>
              <h2 className="font-display text-xl md:text-2xl uppercase tracking-wide text-slate-900">
                Slip Confirmed
              </h2>
              <p className="mt-2 text-slate-600 text-sm md:text-base">
                PA successfully read and confirmed your bets. Sports-data
                analysis will be connected in the next step.
              </p>
            </div>
          </div>
          <div className="mt-5">
            <button
              type="button"
              onClick={clearFile}
              data-testid="analyzer-start-over-btn"
              className="pa-btn pa-btn-ghost"
            >
              <RefreshCw size={16} /> Start Over
            </button>
          </div>
        </section>
      )}

      {/* Step 4A: PA Data Check card (verified sports data, first supported leg) */}
      <AnalyzerDataCheck
        slip={extractedSlip}
        active={step === "confirmed_success" && !!extractedSlip}
      />

      {step === "upload" && (
        <section
          data-testid="analyzer-results-placeholder"
          className="pa-card p-6 md:p-8 mt-6 md:mt-8"
        >
          <h2 className="font-display text-xl md:text-2xl uppercase tracking-wide text-slate-900">
            Your Analysis Will Appear Here
          </h2>
          <p className="mt-2 text-slate-600 text-sm md:text-base">
            Once you upload a bet slip, each leg — teams, market, line,
            odds, and any correlations — will be broken down in this space.
          </p>
        </section>
      )}
    </main>
  );
}
